import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { Select } from '@/components/Select'
import { Dialog, Field, GhostButton, INPUT, PrimaryButton } from '@/features/inventory/shared'
import { fmtDate, fmtEur, useT } from '@/i18n'
import { registersMessages } from '@/i18n/messages/registers'
import {
  CAUSED_BY, CAUSES, DAMAGE_KINDS, DAMAGE_STATUSES, PERMIT_KINDS, PERMIT_STATUSES,
  type AuPermitFee, type ClaimKind, type ClaimStatus, type ClaimView, type DamageView, type EvidenceDay, type PermitState, type PermitView, type ProjectRegisters, type RegisterAlert,
} from './types'

type RT = ReturnType<typeof useRT>
function useRT() { return useT(registersMessages) }
type Key = keyof typeof registersMessages.en
const k = (t: RT, key: string) => t(key as Key)
const todayIso = () => new Date().toISOString().slice(0, 10)
const addDays = (iso: string, n: number) => new Date(Date.parse(`${iso}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10)
const err = (t: RT, e: unknown) => (e instanceof ApiError ? e.message : t('couldNotSave'))
const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')))

const PILL = 'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold'
const TONE = {
  good: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  bad: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  neutral: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
}
const PERMIT_TONE: Record<PermitState, keyof typeof TONE> = { granted: 'good', expiring: 'warn', pending: 'neutral', requested: 'neutral', expired: 'bad', refused: 'bad' }
const CLAIM_TONE: Record<ClaimStatus, keyof typeof TONE> = { draft: 'neutral', notified: 'warn', accepted: 'good', rejected: 'bad', paid: 'good', withdrawn: 'neutral' }

/**
 * The project's registers (docs/project-registers-design.md): permits and utility records,
 * hindrance notices and change orders with crew-day evidence, and damage. They feed the gates in
 * the workflow above, and AU's own traces (permit fees, its old damage log) are offered alongside.
 */
export function RegistersPanel({ projectId }: { projectId: string }) {
  const t = useRT()
  const { data } = useQuery<ProjectRegisters>({
    queryKey: ['registers', projectId], queryFn: () => api.get<ProjectRegisters>(`/api/projects/${projectId}/registers`),
  })
  const [tab, setTab] = useState<'permits' | 'claims' | 'damage'>('permits')
  if (!data) return null
  const permitAlert = data.permits.some((p) => p.state === 'expiring' || p.state === 'expired') || data.workedWithoutPermit.days > 0
  const claimAlert = data.claims.some((c) => c.warning === 'overdue')
  const openDamage = data.damages.filter((d) => d.status === 'open' || d.status === 'claimed').length
  const tabs = [
    { key: 'permits' as const, label: `${t('tabPermits')} (${data.permits.length})`, alert: permitAlert },
    { key: 'claims' as const, label: `${t('tabClaims')} (${data.claims.length})`, alert: claimAlert },
    { key: 'damage' as const, label: `${t('tabDamage')} (${openDamage}/${data.damages.length})`, alert: openDamage > 0 },
  ]
  return (
    <section className="space-y-4 rounded-2xl bg-white p-4 ring-1 ring-neutral-200 sm:p-5 dark:bg-neutral-900 dark:ring-neutral-800">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('title')}</h2>
      <div role="tablist" className="flex flex-wrap gap-1 border-b border-neutral-200 dark:border-neutral-800">
        {tabs.map((x) => (
          <button key={x.key} role="tab" type="button" aria-selected={tab === x.key} onClick={() => setTab(x.key)}
            className={`-mb-px inline-flex min-h-10 items-center gap-1.5 border-b-2 px-3 text-sm ${tab === x.key ? 'border-brand-600 font-semibold text-brand-700 dark:text-brand-300' : 'border-transparent text-neutral-600 hover:text-neutral-900 dark:text-neutral-300'}`}>
            {x.label}{x.alert && <span aria-hidden className="size-2 rounded-full bg-amber-500" />}
          </button>
        ))}
      </div>
      {tab === 'permits' && <Permits t={t} data={data} />}
      {tab === 'claims' && <Claims t={t} data={data} />}
      {tab === 'damage' && <Damage t={t} data={data} />}
    </section>
  )
}

function useSaved(projectId: string, onClose: () => void) {
  const qc = useQueryClient()
  return () => {
    for (const key of [['registers', projectId], ['workflow', projectId], ['workflow-inbox'], ['register-alerts']]) void qc.invalidateQueries({ queryKey: key })
    onClose()
  }
}
const Footer = ({ t, onClose, onSave, pending, disabled }: { t: RT; onClose: () => void; onSave: () => void; pending: boolean; disabled?: boolean }) =>
  <><GhostButton onClick={onClose}>{t('cancel')}</GhostButton><PrimaryButton onClick={onSave} disabled={pending || disabled}>{pending ? t('saving') : t('save')}</PrimaryButton></>
const Err = ({ t, e }: { t: RT; e: unknown }) => (e ? <p role="alert" className="text-sm" style={{ color: 'var(--viz-critical)' }}>{err(t, e)}</p> : null)

// ---- permits -----------------------------------------------------------------------------------

function Permits({ t, data }: { t: RT; data: ProjectRegisters }) {
  const [edit, setEdit] = useState<{ permit?: PermitView; fee?: AuPermitFee } | null>(null)
  const w = data.workedWithoutPermit
  return (
    <div className="space-y-4">
      {w.days > 0 && w.registerFrom && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-500/10 dark:text-red-200">
          {t('workedWithout', { n: w.days, from: fmtDate(w.registerFrom), last: fmtDate(w.last) })}
        </p>
      )}
      <div className="flex justify-end">{data.me.canPermits && <PrimaryButton onClick={() => setEdit({})}>{t('addPermit')}</PrimaryButton>}</div>
      {data.permits.length === 0 ? <p className="text-sm text-neutral-500">{t('nonePermits')}</p> : (
        <ul className="divide-y divide-neutral-100 rounded-xl ring-1 ring-neutral-200 dark:divide-neutral-800 dark:ring-neutral-800">
          {data.permits.map((p) => (
            <li key={p.id} className="flex flex-wrap items-start gap-x-4 gap-y-1 px-3 py-2.5">
              <div className="min-w-0 flex-1 basis-64">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-neutral-500">{k(t, `pk_${p.kind}`)}</span>
                  <span className={`${PILL} ${TONE[PERMIT_TONE[p.state]]}`}>{k(t, `pst_${p.state}`)}</span>
                  {p.auInvoiceId && <span className={`${PILL} ${TONE.neutral}`}>{t('fromAu')}</span>}
                </div>
                <div className="font-medium">{p.authority}{p.reference && <span className="font-normal text-neutral-500"> · {p.reference}</span>}</div>
                {(p.area || p.note) && <div className="text-xs text-neutral-500">{[p.area, p.note].filter(Boolean).join(' · ')}</div>}
              </div>
              <div className="tabular text-sm text-neutral-600 dark:text-neutral-300">
                {p.validFrom || p.validTo ? `${fmtDate(p.validFrom)} – ${p.validTo ? fmtDate(p.validTo) : t('openEnded')}` : p.grantedOn ? fmtDate(p.grantedOn) : p.requestedOn ? fmtDate(p.requestedOn) : ''}
              </div>
              {data.me.canPermits && <button type="button" onClick={() => setEdit({ permit: p })} className="text-xs font-semibold text-brand-700 hover:underline dark:text-brand-300">{t('edit')}</button>}
            </li>
          ))}
        </ul>
      )}
      {data.auPermitFees.length > 0 && (
        <details className="rounded-xl bg-neutral-50 p-3 text-sm dark:bg-neutral-800/50">
          <summary className="cursor-pointer font-medium">{t('auFees')} ({data.auPermitFees.length})</summary>
          <p className="mt-1 text-xs text-neutral-500">{t('auFeesHint')}</p>
          <ul className="mt-2 space-y-1">
            {data.auPermitFees.map((f) => (
              <li key={f.invoiceId} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="tabular text-xs text-neutral-500">{fmtDate(f.issuedOn)}</span>
                <span>{f.authority}{f.reference && <span className="text-neutral-500"> · {f.reference}</span>}</span>
                <span className="text-xs text-neutral-500">{k(t, `pk_${f.kind}`)} · {fmtEur(f.amount, 2)}</span>
                {data.me.canPermits && <button type="button" onClick={() => setEdit({ fee: f })} className="ml-auto text-xs font-semibold text-brand-700 hover:underline dark:text-brand-300">{t('takeOver')}</button>}
              </li>
            ))}
          </ul>
        </details>
      )}
      {edit && <PermitDialog t={t} projectId={data.projectId} permit={edit.permit} fee={edit.fee} onClose={() => setEdit(null)} />}
    </div>
  )
}

function PermitDialog({ t, projectId, permit, fee, onClose }: { t: RT; projectId: string; permit?: PermitView | undefined; fee?: AuPermitFee | undefined; onClose: () => void }) {
  const [f, setF] = useState({
    kind: permit?.kind ?? fee?.kind ?? 'dig', authority: permit?.authority ?? fee?.authority ?? '', reference: permit?.reference ?? fee?.reference ?? '',
    area: permit?.area ?? '', status: permit?.status ?? (fee ? 'granted' : 'requested'), requestedOn: permit?.requestedOn ?? '',
    grantedOn: permit?.grantedOn ?? fee?.issuedOn ?? '', validFrom: permit?.validFrom ?? '', validTo: permit?.validTo ?? '', note: permit?.note ?? '',
  })
  const set = (key: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [key]: e.target.value }))
  const saved = useSaved(projectId, onClose)
  const body = { ...f, reference: f.reference || null, area: f.area || null, note: f.note || null, requestedOn: f.requestedOn || null,
    grantedOn: f.grantedOn || null, validFrom: f.validFrom || null, validTo: f.validTo || null, auInvoiceId: permit?.auInvoiceId ?? fee?.invoiceId ?? null }
  const save = useMutation({
    mutationFn: () => (permit ? api.put(`/api/projects/${projectId}/permits/${permit.id}`, body) : api.post(`/api/projects/${projectId}/permits`, body)),
    onSuccess: saved,
  })
  const del = useMutation({ mutationFn: () => api.del(`/api/projects/${projectId}/permits/${permit!.id}`), onSuccess: saved })
  return (
    <Dialog title={permit ? `${t('edit')}: ${permit.authority}` : t('addPermit')} onClose={onClose}
      footer={<>{permit && <GhostButton onClick={() => del.mutate()} className="mr-auto text-red-700">{t('remove')}</GhostButton>}<Footer t={t} onClose={onClose} onSave={() => save.mutate()} pending={save.isPending} disabled={!f.authority.trim()} /></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('kind')}><Select id="rg-pk" value={f.kind} onChange={(v) => set('kind')({ target: { value: v } })}
          options={PERMIT_KINDS.map((x) => ({ value: x, label: k(t, `pk_${x}`) }))} /></Field>
        <Field label={t('status')}><Select id="rg-ps" value={f.status} onChange={(v) => set('status')({ target: { value: v } })}
          options={PERMIT_STATUSES.map((x) => ({ value: x, label: k(t, `ps_${x}`) }))} /></Field>
        <Field label={t('authority')}><input id="rg-pa" className={INPUT} value={f.authority} onChange={set('authority')} maxLength={200} /></Field>
        <Field label={t('reference')}><input id="rg-pr" className={INPUT} value={f.reference} onChange={set('reference')} maxLength={120} /></Field>
        <div className="sm:col-span-2"><Field label={t('area')}><input id="rg-par" className={INPUT} value={f.area} onChange={set('area')} maxLength={500} /></Field></div>
        <Field label={t('requestedOn')}><input id="rg-prq" type="date" className={INPUT} value={f.requestedOn} onChange={set('requestedOn')} /></Field>
        <Field label={t('grantedOn')}><input id="rg-pg" type="date" className={INPUT} value={f.grantedOn} onChange={set('grantedOn')} /></Field>
        <Field label={t('validFrom')}><input id="rg-pvf" type="date" className={INPUT} value={f.validFrom} onChange={set('validFrom')} /></Field>
        <Field label={t('validTo')}><input id="rg-pvt" type="date" className={INPUT} value={f.validTo} onChange={set('validTo')} /></Field>
        <div className="sm:col-span-2"><Field label={t('note')}><input id="rg-pn" className={INPUT} value={f.note} onChange={set('note')} maxLength={1000} /></Field></div>
      </div>
      <Err t={t} e={save.error ?? del.error} />
    </Dialog>
  )
}

// ---- claims ------------------------------------------------------------------------------------

function Claims({ t, data }: { t: RT; data: ProjectRegisters }) {
  const [edit, setEdit] = useState<{ kind: ClaimKind; claim?: ClaimView } | null>(null)
  const [status, setStatus] = useState<{ claim: ClaimView; to: ClaimStatus } | null>(null)
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-xl text-xs text-neutral-500">{t('noticeHint')}{data.extraWorkBilled > 0 && <> · {t('extraBilled', { amount: fmtEur(data.extraWorkBilled) })}</>}</p>
        {data.me.canDraftClaims && (
          <div className="flex flex-wrap gap-2">
            <PrimaryButton onClick={() => setEdit({ kind: 'hindrance' })}>{t('addHindrance')}</PrimaryButton>
            <GhostButton onClick={() => setEdit({ kind: 'change' })}>{t('addChange')}</GhostButton>
          </div>
        )}
      </div>
      {data.claims.length === 0 ? <p className="text-sm text-neutral-500">{t('noneClaims')}</p> : (
        <ul className="divide-y divide-neutral-100 rounded-xl ring-1 ring-neutral-200 dark:divide-neutral-800 dark:ring-neutral-800">
          {data.claims.map((c) => (
            <li key={c.id} className="space-y-1.5 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="tabular text-xs font-semibold text-neutral-500">{c.label}</span>
                <span className="font-medium">{c.title}</span>
                <span className={`${PILL} ${TONE[CLAIM_TONE[c.status]]}`}>{k(t, `cs_${c.status}`)}</span>
                {c.warning && <span className={`${PILL} ${c.warning === 'overdue' ? TONE.bad : TONE.warn}`}>{k(t, `w_${c.warning}`)}</span>}
              </div>
              <div className="flex flex-wrap gap-x-3 text-xs text-neutral-500">
                <span>{k(t, `ck_${c.kind}`)} · {k(t, `cause_${c.cause}`)}</span>
                <span className="tabular">{fmtDate(c.occurredOn)}{c.endedOn ? ` – ${fmtDate(c.endedOn)}` : ''}</span>
                {c.notifiedOn && <span>{t('notifiedOn')}: {fmtDate(c.notifiedOn)}</span>}
                {c.amountClaimed != null && <span>{t('amountClaimed')}: {fmtEur(c.amountClaimed)}</span>}
                {c.amountAgreed != null && <span>{t('amountAgreed')}: {fmtEur(c.amountAgreed)}</span>}
                {c.sessionIds.length > 0 && <span>{t('evidenceCount', { n: c.sessionIds.length })}</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                <a href={`/api/projects/${data.projectId}/claims/${c.id}/notice.pdf`} className="rounded-lg bg-neutral-100 px-2.5 py-1 text-xs font-semibold hover:bg-neutral-200 dark:bg-neutral-800">{t('letter')}</a>
                {data.me.canDraftClaims && (c.status === 'draft' || c.status === 'rejected') &&
                  <button type="button" onClick={() => setEdit({ kind: c.kind, claim: c })} className="rounded-lg px-2.5 py-1 text-xs font-semibold text-brand-700 ring-1 ring-brand-600/40 dark:text-brand-300">{t('edit')}</button>}
                {data.me.canDecideClaims && c.next.map((to) => (
                  <button key={to} type="button" onClick={() => setStatus({ claim: c, to })} className="rounded-lg px-2.5 py-1 text-xs font-medium ring-1 ring-neutral-300 hover:bg-neutral-50 dark:ring-neutral-700 dark:hover:bg-neutral-800">{k(t, `ca_${to}`)}</button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
      {edit && <ClaimDialog t={t} projectId={data.projectId} kind={edit.kind} claim={edit.claim} onClose={() => setEdit(null)} />}
      {status && <StatusDialog t={t} projectId={data.projectId} claim={status.claim} to={status.to} onClose={() => setStatus(null)} />}
    </div>
  )
}

function ClaimDialog({ t, projectId, kind, claim, onClose }: { t: RT; projectId: string; kind: ClaimKind; claim?: ClaimView | undefined; onClose: () => void }) {
  const [f, setF] = useState({
    title: claim?.title ?? '', description: claim?.description ?? '', cause: claim?.cause ?? (kind === 'hindrance' ? 'permit' : 'client'),
    occurredOn: claim?.occurredOn ?? todayIso(), endedOn: claim?.endedOn ?? '', amountClaimed: claim?.amountClaimed?.toString() ?? '',
  })
  const [picked, setPicked] = useState<Set<string>>(new Set(claim?.sessionIds ?? []))
  const set = (key: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [key]: e.target.value }))
  const from = addDays(f.occurredOn, -7)
  const to = f.endedOn ? addDays(f.endedOn, 3) : addDays(f.occurredOn, 21)
  const { data: days } = useQuery<EvidenceDay[]>({
    queryKey: ['evidence', projectId, from, to], enabled: !!f.occurredOn,
    queryFn: () => api.get<EvidenceDay[]>(`/api/projects/${projectId}/evidence?from=${from}&to=${to < todayIso() ? to : todayIso()}`),
  })
  const saved = useSaved(projectId, onClose)
  const body = { kind, title: f.title, description: f.description || null, cause: f.cause, occurredOn: f.occurredOn, endedOn: f.endedOn || null,
    amountClaimed: num(f.amountClaimed), blockId: claim?.blockId ?? null, sessionIds: [...picked],
    scanIds: (days ?? []).filter((d) => picked.has(d.sessionId)).flatMap((d) => d.scans.map((s) => s.id)) }
  const save = useMutation({
    mutationFn: () => (claim ? api.put(`/api/projects/${projectId}/claims/${claim.id}`, body) : api.post(`/api/projects/${projectId}/claims`, body)),
    onSuccess: saved,
  })
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })
  return (
    <Dialog title={claim ? `${claim.label}: ${claim.title}` : k(t, kind === 'hindrance' ? 'addHindrance' : 'addChange')} hint={t('noticeHint')} onClose={onClose}
      footer={<Footer t={t} onClose={onClose} onSave={() => save.mutate()} pending={save.isPending} disabled={!f.title.trim()} />}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2"><Field label={t('titleField')}><input id="rg-ct" className={INPUT} value={f.title} onChange={set('title')} maxLength={200} /></Field></div>
        <Field label={t('cause')}><Select id="rg-cc" value={f.cause} onChange={(v) => set('cause')({ target: { value: v } })}
          options={CAUSES.map((x) => ({ value: x, label: k(t, `cause_${x}`) }))} /></Field>
        <Field label={t('amountClaimed')}><input id="rg-ca" inputMode="decimal" className={INPUT} value={f.amountClaimed} onChange={set('amountClaimed')} /></Field>
        <Field label={kind === 'hindrance' ? t('occurredOn') : t('occurredOnChange')}><input id="rg-co" type="date" className={INPUT} value={f.occurredOn} onChange={set('occurredOn')} /></Field>
        {kind === 'hindrance' && <Field label={t('endedOn')}><input id="rg-ce" type="date" className={INPUT} value={f.endedOn} onChange={set('endedOn')} /></Field>}
        <div className="sm:col-span-2"><Field label={t('description')}><textarea id="rg-cd" rows={3} className={INPUT} value={f.description} onChange={set('description')} maxLength={4000} /></Field></div>
      </div>
      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">{t('evidence')}</span>
          <span className="text-xs text-neutral-500">{t('picked', { n: picked.size })}</span>
        </div>
        <p className="text-xs text-neutral-500">{t('evidenceHint')}</p>
        {days && days.length === 0 && <p className="text-sm text-neutral-500">{t('noEvidence')}</p>}
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {(days ?? []).map((d) => (
            <li key={d.sessionId}>
              <label className={`flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 text-sm ring-1 ${picked.has(d.sessionId) ? 'bg-brand-50 ring-brand-600/40 dark:bg-brand-500/10' : 'ring-neutral-200 dark:ring-neutral-700'}`}>
                <input type="checkbox" className="mt-1" checked={picked.has(d.sessionId)} onChange={() => toggle(d.sessionId)} />
                <span className="min-w-0 flex-1">
                  <span className="tabular font-medium">{fmtDate(d.workDate)}</span> <span className="text-neutral-500">· {d.crew ?? '—'}{d.streets ? ` · ${d.streets}` : ''}</span>
                  {d.comment && <span className="block text-xs italic text-neutral-700 dark:text-neutral-300">„{d.comment}“</span>}
                  <span className="block text-[11px] text-neutral-500">
                    {[d.reports && t('reports', { n: d.reports }), d.rtkSegments && t('rtk', { n: d.rtkSegments }), d.scans.length && t('lidar', { n: d.scans.length })].filter(Boolean).join(' · ')}
                  </span>
                  {(d.auFiles ?? []).length > 0 && (
                    <span className="mt-0.5 flex flex-wrap gap-2 text-[11px]">
                      {(d.auFiles ?? []).map((f, i) => (
                        <a key={i} href={f.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="font-semibold text-brand-700 hover:underline dark:text-brand-300">
                          {k(t, f.kind === 'daily_report' ? 'dailyReport' : 'sitePhoto')}{f.comment ? `: ${f.comment}` : ''}
                        </a>
                      ))}
                    </span>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
      <Err t={t} e={save.error} />
    </Dialog>
  )
}

function StatusDialog({ t, projectId, claim, to, onClose }: { t: RT; projectId: string; claim: ClaimView; to: ClaimStatus; onClose: () => void }) {
  const [notifiedOn, setNotifiedOn] = useState(claim.notifiedOn ?? todayIso())
  const [agreed, setAgreed] = useState(claim.amountAgreed?.toString() ?? claim.amountClaimed?.toString() ?? '')
  const saved = useSaved(projectId, onClose)
  const save = useMutation({
    mutationFn: () => api.post(`/api/projects/${projectId}/claims/${claim.id}/status`, {
      status: to, notifiedOn: to === 'notified' ? notifiedOn : null, amountAgreed: to === 'accepted' || to === 'paid' ? num(agreed) : null,
    }),
    onSuccess: saved,
  })
  return (
    <Dialog title={`${claim.label}: ${k(t, `ca_${to}`)}`} onClose={onClose} footer={<Footer t={t} onClose={onClose} onSave={() => save.mutate()} pending={save.isPending} />}>
      {to === 'notified' && <Field label={t('notifiedOn')}><input id="rg-sn" type="date" className={INPUT} value={notifiedOn} max={todayIso()} onChange={(e) => setNotifiedOn(e.target.value)} /></Field>}
      {(to === 'accepted' || to === 'paid') && claim.kind === 'change' &&
        <Field label={t('amountAgreed')}><input id="rg-sa" inputMode="decimal" className={INPUT} value={agreed} onChange={(e) => setAgreed(e.target.value)} /></Field>}
      <Err t={t} e={save.error} />
    </Dialog>
  )
}

// ---- damage ------------------------------------------------------------------------------------

function Damage({ t, data }: { t: RT; data: ProjectRegisters }) {
  const [edit, setEdit] = useState<{ damage?: DamageView } | null>(null)
  return (
    <div className="space-y-4">
      <div className="flex justify-end">{data.me.canDamage && <PrimaryButton onClick={() => setEdit({})}>{t('addDamage')}</PrimaryButton>}</div>
      {data.damages.length === 0 ? <p className="text-sm text-neutral-500">{t('noneDamage')}</p> : (
        <ul className="divide-y divide-neutral-100 rounded-xl ring-1 ring-neutral-200 dark:divide-neutral-800 dark:ring-neutral-800">
          {data.damages.map((d) => (
            <li key={d.id} className="flex flex-wrap items-start gap-x-4 gap-y-1 px-3 py-2.5">
              <div className="min-w-0 flex-1 basis-64">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="tabular text-xs text-neutral-500">{fmtDate(d.occurredOn)}</span>
                  <span className="text-xs font-semibold">{k(t, `dk_${d.kind}`)}</span>
                  <span className={`${PILL} ${d.status === 'open' || d.status === 'claimed' ? TONE.warn : TONE.good}`}>{k(t, `ds_${d.status}`)}</span>
                  {d.daysOpen != null && d.daysOpen > 14 && <span className={`${PILL} ${TONE.bad}`}>{t('daysOpen', { n: d.daysOpen })}</span>}
                </div>
                <div className="text-sm">{d.description}</div>
                <div className="text-xs text-neutral-500">{[d.owner, d.location, k(t, `cb_${d.causedBy}`), d.costEstimate != null ? fmtEur(d.costEstimate) : null, d.insurance ? t('insurance') : null].filter(Boolean).join(' · ')}</div>
              </div>
              {data.me.canDamage && <button type="button" onClick={() => setEdit({ damage: d })} className="text-xs font-semibold text-brand-700 hover:underline dark:text-brand-300">{t('edit')}</button>}
            </li>
          ))}
        </ul>
      )}
      {data.auDamages.length > 0 && (
        <details className="rounded-xl bg-neutral-50 p-3 text-sm dark:bg-neutral-800/50">
          <summary className="cursor-pointer font-medium">{t('auDamages')} ({data.auDamages.length})</summary>
          <ul className="mt-2 space-y-1">
            {data.auDamages.map((a) => <li key={a.recordId}><span className="tabular text-xs text-neutral-500">{fmtDate(a.occurredOn)}</span> {[a.street, a.city].filter(Boolean).join(', ')} · {a.description} {a.damagedParty && <span className="text-neutral-500">· {a.damagedParty}</span>}</li>)}
          </ul>
        </details>
      )}
      {edit && <DamageDialog t={t} projectId={data.projectId} damage={edit.damage} onClose={() => setEdit(null)} />}
    </div>
  )
}

function DamageDialog({ t, projectId, damage, onClose }: { t: RT; projectId: string; damage?: DamageView | undefined; onClose: () => void }) {
  const [f, setF] = useState({
    occurredOn: damage?.occurredOn ?? todayIso(), kind: damage?.kind ?? 'telecom', owner: damage?.owner ?? '', location: damage?.location ?? '',
    description: damage?.description ?? '', causedBy: damage?.causedBy ?? 'own_crew', reportedOn: damage?.reportedOn ?? '', status: damage?.status ?? 'open',
    costEstimate: damage?.costEstimate?.toString() ?? '', insurance: damage?.insurance ?? false,
  })
  const set = (key: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [key]: e.target.value }))
  const saved = useSaved(projectId, onClose)
  const body = { ...f, owner: f.owner || null, location: f.location || null, reportedOn: f.reportedOn || null, costEstimate: num(f.costEstimate),
    sessionId: damage?.sessionId ?? null }
  const save = useMutation({
    mutationFn: () => (damage ? api.put(`/api/projects/${projectId}/damages/${damage.id}`, body) : api.post(`/api/projects/${projectId}/damages`, body)),
    onSuccess: saved,
  })
  return (
    <Dialog title={damage ? `${t('edit')}: ${k(t, `dk_${damage.kind}`)}` : t('addDamage')} onClose={onClose}
      footer={<Footer t={t} onClose={onClose} onSave={() => save.mutate()} pending={save.isPending} disabled={!f.description.trim()} />}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('occurredOn')}><input id="rg-do" type="date" className={INPUT} value={f.occurredOn} max={todayIso()} onChange={set('occurredOn')} /></Field>
        <Field label={t('kind')}><Select id="rg-dk" value={f.kind} onChange={(v) => set('kind')({ target: { value: v } })}
          options={DAMAGE_KINDS.map((x) => ({ value: x, label: k(t, `dk_${x}`) }))} /></Field>
        <Field label={t('owner')}><input id="rg-dow" className={INPUT} value={f.owner} onChange={set('owner')} maxLength={200} /></Field>
        <Field label={t('location')}><input id="rg-dl" className={INPUT} value={f.location} onChange={set('location')} maxLength={300} /></Field>
        <div className="sm:col-span-2"><Field label={t('description')}><textarea id="rg-dd" rows={3} className={INPUT} value={f.description} onChange={set('description')} maxLength={4000} /></Field></div>
        <Field label={t('causedBy')}><Select id="rg-dc" value={f.causedBy} onChange={(v) => set('causedBy')({ target: { value: v } })}
          options={CAUSED_BY.map((x) => ({ value: x, label: k(t, `cb_${x}`) }))} /></Field>
        <Field label={t('status')}><Select id="rg-ds" value={f.status} onChange={(v) => set('status')({ target: { value: v } })}
          options={DAMAGE_STATUSES.map((x) => ({ value: x, label: k(t, `ds_${x}`) }))} /></Field>
        <Field label={t('reportedOn')}><input id="rg-dr" type="date" className={INPUT} value={f.reportedOn} onChange={set('reportedOn')} /></Field>
        <Field label={t('costEstimate')}><input id="rg-dce" inputMode="decimal" className={INPUT} value={f.costEstimate} onChange={set('costEstimate')} /></Field>
        <label className="flex items-center gap-2 text-sm sm:col-span-2"><input id="rg-di" type="checkbox" checked={f.insurance} onChange={(e) => setF((x) => ({ ...x, insurance: e.target.checked }))} />{t('insurance')}</label>
      </div>
      <Err t={t} e={save.error} />
    </Dialog>
  )
}

/** Register alerts for "My work": permits expiring, notices overdue, damage left open. */
export function RegisterAlerts() {
  const t = useRT()
  const { data } = useQuery<RegisterAlert[]>({ queryKey: ['register-alerts'], queryFn: () => api.get('/api/registers/alerts') })
  if (!data || data.length === 0) return null
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{t('alertsTitle')} ({data.length})</h2>
      <ul className="divide-y divide-neutral-100 overflow-hidden rounded-2xl bg-white ring-1 ring-neutral-200 dark:divide-neutral-800 dark:bg-neutral-900 dark:ring-neutral-800">
        {data.map((a, i) => (
          <li key={`${a.projectId}-${a.kind}-${i}`}>
            <Link to={`/projects/${a.projectId}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
              <span className={`${PILL} ${a.kind === 'permit_expiring' ? TONE.warn : TONE.bad}`}>{k(t, `al_${a.kind}`)}</span>
              <span className="font-medium">{a.projectName ?? '—'}</span>
              <span className="min-w-0 flex-1 truncate text-sm text-neutral-600 dark:text-neutral-300">{a.label}</span>
              <span className="tabular text-xs text-neutral-500">{fmtDate(a.date)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
